// Lógica PURA de Compromisos: el corte de las pastillas, los filtros, el orden y los derivados de la
// barra de herramientas. Recibe filas y devuelve filas — sin React y sin fetch. El día de hoy entra
// por parámetro para que la pantalla y los tests midan contra el mismo instante.
import {
    ANY_FILTER,
    type PaymentPromiseRow,
    type PromiseStatKey,
    type PromiseStats,
    type PromisesFilters,
    type PromisesSummary,
} from './types';

/** Corre `delta` días de calendario sobre un `YYYY-MM-DD`, en UTC para no cruzar husos. */
export function shiftDay(day: string, delta: number): string {
    const date = new Date(`${day}T00:00:00.000Z`);
    date.setUTCDate(date.getUTCDate() + delta);
    return date.toISOString().slice(0, 10);
}

/** El corte de UNA pastilla. «Hoy» y «Mañana» son el día prometido, no el estado. */
export function applyStatFilter(
    rows: readonly PaymentPromiseRow[],
    stat: PromiseStatKey,
    today: string,
): PaymentPromiseRow[] {
    switch (stat) {
        case 'all':
            return [...rows];
        case 'today':
            return rows.filter((row) => row.promisedDay === today);
        case 'tomorrow':
            return rows.filter((row) => row.promisedDay === shiftDay(today, 1));
        case 'broken':
            return rows.filter((row) => row.status === 'broken');
        case 'kept':
            return rows.filter((row) => row.status === 'kept');
    }
}

/** Los conteos de las pastillas salen del universo COMPLETO, no de lo que dejaron los filtros. */
export function computePromiseStats(
    rows: readonly PaymentPromiseRow[],
    today: string,
): PromiseStats {
    return {
        all: rows.length,
        today: applyStatFilter(rows, 'today', today).length,
        tomorrow: applyStatFilter(rows, 'tomorrow', today).length,
        broken: applyStatFilter(rows, 'broken', today).length,
        kept: applyStatFilter(rows, 'kept', today).length,
    };
}

function matchesSearch(row: PaymentPromiseRow, search: string): boolean {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    const haystack = `${row.customerName} ${row.creditId} ${row.promiseId}`.toLowerCase();
    return haystack.includes(needle);
}

export function filterPromises(
    rows: readonly PaymentPromiseRow[],
    filters: PromisesFilters,
): PaymentPromiseRow[] {
    return rows.filter((row) => {
        if (!matchesSearch(row, filters.search)) return false;
        if (filters.channel !== ANY_FILTER && row.channel !== filters.channel) return false;
        if (filters.fromDay && row.promisedDay < filters.fromDay) return false;
        if (filters.toDay && row.promisedDay > filters.toDay) return false;
        if (filters.minAmountCents !== null && row.promisedAmountCents < filters.minAmountCents) {
            return false;
        }
        if (filters.maxAmountCents !== null && row.promisedAmountCents > filters.maxAmountCents) {
            return false;
        }
        return true;
    });
}

/** El orden del diseño: lo más reciente arriba, y el folio desempata dentro del mismo día. */
export function sortPromises(rows: readonly PaymentPromiseRow[]): PaymentPromiseRow[] {
    return [...rows].sort((a, b) =>
        `${b.promisedDay}${b.promiseId}`.localeCompare(`${a.promisedDay}${a.promiseId}`),
    );
}

/**
 * Cure rate del set FILTRADO: cumplidos sobre los que ya cerraron. Un compromiso activo todavía no
 * cumplió ni incumplió, así que no cuenta ni arriba ni abajo. Sin cerrados el diseño pinta 0%.
 */
export function summarizePromises(rows: readonly PaymentPromiseRow[]): PromisesSummary {
    const closed = rows.filter((row) => row.status === 'kept' || row.status === 'broken');
    const kept = closed.filter((row) => row.status === 'kept');
    return {
        count: rows.length,
        totalAmountCents: rows.reduce((sum, row) => sum + row.promisedAmountCents, 0),
        cureRatePct: closed.length === 0 ? 0 : Math.round((kept.length / closed.length) * 100),
    };
}

/**
 * Los cuatro atajos de fecha del diseño. Negativo limpia el rango («Todo»); el resto arranca HOY y
 * llega `days` días adelante, así que «Mañana» también deja pasar lo de hoy, como el mock.
 */
export function dateShortcutRange(days: number, today: string): { fromDay: string; toDay: string } {
    if (days < 0) return { fromDay: '', toDay: '' };
    return { fromDay: today, toDay: shiftDay(today, days) };
}

/** Cuántos filtros están puestos: la barra de herramientas solo se pinta cuando hay alguno. */
export function activeFilterCount(filters: PromisesFilters): number {
    let total = 0;
    if (filters.search.trim()) total += 1;
    if (filters.channel !== ANY_FILTER) total += 1;
    if (filters.fromDay) total += 1;
    if (filters.toDay) total += 1;
    if (filters.minAmountCents !== null) total += 1;
    if (filters.maxAmountCents !== null) total += 1;
    return total;
}
